<template>
    <v-container fluid class="pa-3" v-if="sessionActive">

        <material-header title="Test ground" info="Place where experimental things blow up!"
                         icon="mdi-seat-recline-extra"></material-header>
        <v-alert v-if="!hasRole('Super Admin')"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions to use this feature
        </v-alert>

        <v-row v-if="hasRole('Super Admin')" dense class="pt-1">

            <v-col cols="12">
                <cluster-stats></cluster-stats>
            </v-col>

            <v-col cols="12">
                <title-card title="Sample Cycle Monitor">
                    <v-container fluid>
                        <v-row no-gutters>
                            <v-col cols=1>

                                <v-btn v-if="sampleCyclesLoading === null" class="mt-5" small
                                       @click="getSampleCycles" color="primary">Fetch
                                </v-btn>

                                <v-progress-circular v-else
                                                     indeterminate
                                                     size="90"
                                                     width="5"
                                                     color="primary">
                                    <span class="grey--text text--lighten-2">LIVE</span>
                                </v-progress-circular>

                            </v-col>
                            <v-col offset="8" cols="3" v-if="nextSampleCycleAt" class="pt-5">
                                <v-alert dense type="info" class="body-2">
                                    Next sample cycle {{ toHumanTill(nextSampleCycleAt) }}
                                </v-alert>
                            </v-col>
                        </v-row>
                    </v-container>


                    <v-data-table
                        :headers="headers"
                        :items="sampleCycles"
                        :items-per-page="100"
                        dense
                        class="elevation-1">

                        <template v-slot:body="{ items }">
                            <tbody>
                            <tr v-for="item in items" :key="item.id">

                                <td>
                                    {{ item.project_id }}
                                </td>

                                <td>
                                    <div>

                                        <v-chip small
                                                v-if="item.process_queued_at === null && !item.processed"
                                                color="grey darken-2">
                                            Waiting
                                        </v-chip>

                                        <v-chip small
                                                v-if="item.process_queued_at !== null && item.process_started_at === null && !item.processed"
                                                color="warning">
                                            Queued
                                        </v-chip>

                                        <v-chip small
                                                v-if="item.process_queued_at !== null && item.process_started_at !== null && !item.processed"
                                                color="primary">
                                            Running
                                        </v-chip>

                                        <v-chip small
                                                v-if="item.process_started_at !== null && item.process_failed===true && item.processed"
                                                color="red">
                                            FAILED
                                        </v-chip>

                                        <v-chip small
                                                v-if="item.process_started_at !== null && item.process_failed===false && item.processed"
                                                color="green">
                                            Complete
                                        </v-chip>

                                    </div>
                                </td>

                                <td>
                                    {{ calculateDelay(item) }}
                                </td>

                                <td>
                                    {{ item.priority }}
                                </td>

                                <td>
                                    {{ item.invited_sample_time }} ms
                                </td>

                                <td>
                                    {{ item.needed_count }}
                                </td>

                                <td>
                                    {{ item.available_count }}
                                </td>

                                <td>
                                    <v-chip small
                                            color="grey darken-2"
                                            v-if="!item.invited_time && item.process_created && item.sample_time && !item.processed">
                                        PROCESSING...
                                    </v-chip>

                                    <v-chip small
                                            color="grey darken-2"
                                            v-if="!item.invited_time && item.process_created && item.sample_time && item.processed">
                                        N/A
                                    </v-chip>

                                    <span v-if="item.invited_time!==null">
                                        {{ item.invited_time }} s
                                    </span>

                                    <span v-else>
                                        --
                                    </span>
                                </td>

                                <td>
                                    {{ item.invited_count }}
                                </td>

                                <td>
                                    {{ item.invited_error_count }}
                                </td>

                                <td>
                                    {{ item.invited_locale_error_count }}
                                </td>

                                <td>
                                    {{ item.reminded_sample_time }} ms
                                </td>

                                <td>
                                    {{ item.reminded_time }} s
                                </td>

                                <td>
                                    {{ item.reminded_count }}
                                </td>

                                <td>
                                    {{ item.reminded_error_count }}
                                </td>

                                <td>
                                    {{ item.reminded_locale_error_count }}
                                </td>

                                <td>
                                    {{ item.project_duplicates }}
                                </td>

                                <td>
                                    {{ item.cycle_duplicates }}
                                </td>

                                <td>
                                    {{ item.completed_count }}
                                </td>

                                <td>
                                    <v-chip small color="red" v-if="!item.processed">
                                        FALSE
                                    </v-chip>
                                    <v-chip small v-else color="green">
                                        TRUE
                                    </v-chip>
                                </td>

                                <td>
                                    <v-chip label small class="body-2" color="primary" v-if="item.processed===false">
                                        <span v-if="item.process_in_seconds === null">
                                            NOW
                                        </span>
                                        <span v-if="item.process_in_seconds !==null">
                                            {{ item.process_in_seconds }} s
                                        </span>

                                    </v-chip>
                                </td>

                            </tr>
                            </tbody>
                        </template>
                    </v-data-table>
                </title-card>
            </v-col>

            <v-col cols="12">

                <v-container fluid class="pa-0">
                    <v-row no-gutters>

                        <v-col cols="12" v-if="nodes.length > 0" v-for="(node, index) in nodes" :key="index">

                            <title-card :no-padding="true" :title="'Node ' + (index+1)">

                                <v-container fluid>
                                    <v-row dense>
                                        <v-col cols=6>
                                            <node-stats :stats="node.search" statName="Search"></node-stats>
                                        </v-col>

                                        <v-col cols=6>
                                            <node-stats :stats="node.write" statName="Write"></node-stats>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </title-card>

                        </v-col>
                    </v-row>
                </v-container>
            </v-col>

        </v-row>

    </v-container>

</template>

<script>

import Json from 'vue-json-pretty';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import axios from "axios";
import {timeDisplay} from "../../../components/mixins/timeDisplayMixin";
import moment from 'moment';
import NodeStats from "./NodeStats";
import TitleCard from "../../../components/UI/TitleCard";
import ClusterStats from "./ClusterStats";

export default {

    mixins: [activeSessionChecker, timeDisplay],

    components: {
        NodeStats,
        Json,
        MaterialHeader,
        TitleCard,
        ClusterStats
    },

    data() {
        return {
            keys: [],
            toEmail: null,
            dumpData: null,
            statusMenuModel: false,
            sampleCycles: [],
            sampleCyclesLoading: null,
            reloadSampleCycleTimer: null,
            nextSampleCycleAt: null,
            projectId: '',
            nodes: [],
            headers: [
                {
                    text: 'PID',
                    align: 'left',
                    sortable: true,
                    value: 'project_id'
                },
                {
                    text: 'STATUS',
                    align: 'left',
                    sortable: true,
                    value: 'process_created'
                },
                {
                    text: 'DELAY',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'PRIORITY',
                    align: 'left',
                    sortable: true,
                    value: 'priority'
                },
                {
                    text: 'SAMPLE',
                    align: 'left',
                    sortable: true,
                    value: 'sample_time'
                },
                {
                    text: 'NEEDED',
                    align: 'left',
                    sortable: true,
                    value: 'invites_needed'
                },
                {
                    text: 'AVAILABLE',
                    align: 'left',
                    sortable: true,
                    value: 'available_panelists'
                },
                {
                    text: 'INVITE TIME',
                    align: 'left',
                    sortable: true,
                    value: 'invite_time'
                },
                {
                    text: 'INVITED COUNT',
                    align: 'left',
                    sortable: true,
                    value: 'invited_count'
                },
                {
                    text: 'INVITE ERRORS',
                    align: 'left',
                    sortable: true,
                    value: 'invited_count'
                },
                {
                    text: 'INVITE LOCALE ERRORS',
                    align: 'left',
                    sortable: true,
                    value: 'invited_count'
                },
                {
                    text: 'REMINDED SAMPLE',
                    align: 'left',
                    sortable: true,
                    value: 'reminded_sample_time'
                },
                {
                    text: 'REMINDED TIME',
                    align: 'left',
                    sortable: true,
                    value: 'reminded_time'
                },
                {
                    text: 'REMINDED COUNT',
                    align: 'left',
                    sortable: true,
                    value: 'reminded_count'
                },
                {
                    text: 'REMINDED ERRORS',
                    align: 'left',
                    sortable: true,
                    value: 'reminded_error_count'
                },
                {
                    text: 'REMINDED LOCALE_ERRORS',
                    align: 'left',
                    sortable: true,
                    value: 'reminded_locale_error_count'
                },
                {
                    text: 'DUPS',
                    align: 'left',
                    sortable: true,
                    value: 'duplicate_count'
                },
                {
                    text: 'DUPG',
                    align: 'left',
                    sortable: true,
                    value: 'global_duplicate_count'
                },
                {
                    text: 'COMPLETES',
                    align: 'left',
                    sortable: true,
                    value: 'current_completed_count'
                },
                {
                    text: 'DONE',
                    align: 'left',
                    sortable: true,
                    value: 'processed'
                },
                {
                    text: 'RUN TIME',
                    align: 'left',
                    sortable: true,
                    value: 'queue_after'
                },
            ]
        }
    },

    computed: {
        emailModel: {
            get() {
                if (!this.toEmail) {
                    return this.$store.state.user.email;
                } else {
                    return this.toEmail;
                }
            },
            set(value) {
                this.toEmail = value;
            }
        }
    },

    methods: {

        calculateDelay(item) {
            if (item.process_started_at) {
                let delaySeconds = moment.utc(item.process_started_at).diff(item.queue_after, 'seconds');

                if (delaySeconds < 0) delaySeconds = 0;

                return delaySeconds + ' s';
            }
        },

        getESThreadPoolStats() {
            axios.get('/elastic-search/thread-pool/stats')
                .then(({data}) => {

                    this.nodes = data.nodes;

                    Bus.$emit('flash-message', {message: 'Stats obtained'});
                })
                .catch(() => {
                    Bus.$emit('flash-message', {message: 'Well that failed....', type: 'error'});
                });
        },

        getSampleCycles() {

            this.sampleCyclesLoading = true;

            let url = '/sample-cycle';

            if (this.projectId.length > 0) {
                url += '/' + this.projectId;
            }

            axios.get(url)

                .then(({data}) => {
                    this.sampleCycles = data.cycles;
                    this.nextSampleCycleAt = data.next_cycle_at;
                })
                .catch(error => {

                    if (error.response.status === 404) {

                        this.sampleCycles = [];

                        Bus.$emit('flash-message', {message: 'Sample cycle does not exist yet...', type: 'info'});
                    } else {
                        Bus.$emit('flash-message', {message: 'Well that failed....', type: 'error'});
                    }
                })
                .finally(() => {
                    this.sampleCycleLoading = false;
                });

            if (!this.reloadSampleCycleTimer) {
                this.reloadSampleCycleTimer = setInterval(() => {
                    this.getSampleCycles();
                }, 1000);
            }
        },
    },

    mounted() {
        this.getESThreadPoolStats();
    }
}

</script>
